<script>
import { getJobAssigneeIds } from '../../utils/helpers';

const MAX_VISIBLE = 6;

export default {
    name: 'JobAssigneeFilter',
    props: {
        users: {
            type: Array,
            default: () => []
        },
        jobs: {
            type: Array,
            default: () => []
        },
        value: {
            type: Array,
            default: () => []
        },
        currentUserId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            overflowOpen: false
        };
    },
    computed: {
        jobCounts() {
            const counts = {};
            (this.jobs || []).forEach(job => {
                getJobAssigneeIds(job).forEach(id => {
                    counts[id] = (counts[id] || 0) + 1;
                });
            });
            return counts;
        },
        // Users with at least one job (or already selected), current user first, then by job count
        assignedUsers() {
            return (this.users || [])
                .filter(u => u && (this.jobCounts[u.id] || this.value.includes(u.id)))
                .sort((a, b) => {
                    if (a.id === this.currentUserId) return -1;
                    if (b.id === this.currentUserId) return 1;
                    return (this.jobCounts[b.id] || 0) - (this.jobCounts[a.id] || 0);
                });
        },
        visibleUsers() {
            return this.assignedUsers.slice(0, MAX_VISIBLE);
        },
        overflowUsers() {
            return this.assignedUsers.slice(MAX_VISIBLE);
        },
        overflowSelectedCount() {
            return this.overflowUsers.filter(u => this.value.includes(u.id)).length;
        }
    },
    mounted() {
        document.addEventListener('click', this.closeOverflow);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeOverflow);
    },
    methods: {
        isSelected(userId) {
            return this.value.includes(userId);
        },
        toggle(userId) {
            this.$emit('input', this.isSelected(userId)
                ? this.value.filter(id => id !== userId)
                : [...this.value, userId]);
        },
        userLabel(user) {
            const count = this.jobCounts[user.id] || 0;
            return `${user.name || user.email} · ${count} ${count === 1 ? 'job' : 'jobs'}`;
        },
        closeOverflow() {
            this.overflowOpen = false;
        }
    }
};
</script>

<template>
    <div v-if="assignedUsers.length" class="assignee-filter d-flex align-items-center flex-shrink-0">
        <button v-for="user in visibleUsers" :key="user.id" type="button"
            v-b-tooltip.hover.bottom="userLabel(user)"
            class="assignee-filter-avatar" :class="{ selected: isSelected(user.id) }"
            :aria-pressed="isSelected(user.id) ? 'true' : 'false'"
            :aria-label="`Filter jobs by ${userLabel(user)}`"
            @click="toggle(user.id)">
            <img :src="user.profileImageUrl || '/img/avatar.svg'" width="28" height="28" class="rounded-circle" alt="">
        </button>
        <div v-if="overflowUsers.length" class="position-relative">
            <button type="button" class="assignee-filter-avatar assignee-filter-more"
                :class="{ selected: overflowSelectedCount > 0 }"
                :aria-label="`${overflowUsers.length} more users`"
                @click.stop="overflowOpen = !overflowOpen">
                +{{ overflowUsers.length }}
            </button>
            <div class="dropdown-menu rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden"
                :class="{ show: overflowOpen }" @click.stop>
                <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224 assignee-filter-list">
                    <li v-for="user in overflowUsers" :key="user.id" class="list-group-item p-0 bg-white d-flex rounded-0">
                        <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3 w-100"
                            :class="{ 'fw-semibold text-gray-900': isSelected(user.id) }"
                            @click.prevent="toggle(user.id)">
                            <img :src="user.profileImageUrl || '/img/avatar.svg'" width="20" height="20"
                                class="rounded-circle flex-shrink-0" alt="">
                            <span class="text-truncate ml-0">{{ userLabel(user) }}</span>
                            <img v-if="isSelected(user.id)" src="/img/check.svg" width="16" class="d-block ml-auto"
                                alt="selected">
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style scoped>
.assignee-filter-avatar {
    padding: 0;
    border: 2px solid #fff;
    border-radius: 50%;
    background: #fff;
    line-height: 0;
    position: relative;
    transition: transform 0.1s ease;
}

.assignee-filter-avatar + .assignee-filter-avatar,
.assignee-filter-avatar + .position-relative {
    margin-left: -8px;
}

.assignee-filter-avatar img {
    object-fit: cover;
}

.assignee-filter-avatar:hover,
.assignee-filter-avatar:focus-visible {
    z-index: 2;
    transform: translateY(-2px);
}

.assignee-filter-avatar.selected {
    z-index: 1;
    border-color: #019256;
}

.assignee-filter-more {
    width: 32px;
    height: 32px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: #6c757d;
    background: #e9ecef;
}

.assignee-filter-list {
    max-height: 280px;
    overflow-y: auto;
}

.dropdown-item {
    cursor: pointer;
}
</style>
